import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Search,
  Sparkles,
  Send,
  Loader2,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  FileText,
  ChevronDown,
  ChevronUp,
  X,
  MessageSquare,
  Copy,
  Check,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { Badge, Button, Spinner } from '@centraoneplus/ui';
import { ragService, type RagResponse, type RagSource } from '@services/rag.service';
import { knowledgeAnalyticsService } from '@services/knowledge-analytics.service';
import { knowledgeService } from '@services/knowledge.service';
import { daysSince, isStale } from '@features/knowledge/types';

interface RagSearchProps {
  onArticleClick?: (articleId: string) => void;
  placeholder?: string;
  showSuggestions?: boolean;
}

export function RagSearch({
  onArticleClick,
  placeholder = 'Ask a question about our knowledge base...',
  showSuggestions = true,
}: RagSearchProps) {
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [response, setResponse] = useState<RagResponse | null>(null);
  const [streamedAnswer, setStreamedAnswer] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSources, setShowSources] = useState(true);
  const [feedbackSent, setFeedbackSent] = useState<'helpful' | 'not_helpful' | null>(null);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  /**
   * 18d. When each cited ARTICLE was last reviewed, keyed by id.
   *
   * `RagSource` carries a relevance score and nothing about the source's own
   * health, so a confident answer built on an article nobody has opened since
   * March looks exactly like one built on this morning's. The ages are fetched
   * per answer — at most `maxResults` of them — rather than joined against
   * whatever page of the register happens to be loaded, because a source whose
   * age is silently missing reads as a source that is fine.
   */
  const [reviewedAt, setReviewedAt] = useState<Record<string, string>>({});
  const abortRef = useRef<(() => void) | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Stable id so a search and its subsequent result-click are linked for CTR/deflection.
  const sessionIdRef = useRef<string>(crypto.randomUUID());
  const queryIdRef = useRef<string>(crypto.randomUUID());
  const submittedQueryRef = useRef<string>('');

  // Fetch suggestions on query change
  useEffect(() => {
    if (!showSuggestions || query.length < 3) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const results = await ragService.getSuggestions(query, 5);
        setSuggestions(results);
      } catch {
        // Ignore suggestion errors
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, showSuggestions]);

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!query.trim() || isLoading) return;

    submittedQueryRef.current = query.trim();
    queryIdRef.current = crypto.randomUUID(); // Generate new query ID for feedback tracking
    setIsLoading(true);
    setIsStreaming(true);
    setError(null);
    setResponse(null);
    setStreamedAnswer('');
    setFeedbackSent(null);
    setFeedbackLoading(false);
    setCopied(false);
    setSuggestions([]);

    try {
      // Use streaming for real-time response
      abortRef.current = ragService.queryStream(
        { query, includeContext: true, maxResults: 5 },
        (chunk) => {
          setStreamedAnswer((prev) => prev + chunk);
        },
        (fullResponse) => {
          setResponse(fullResponse);
          setIsStreaming(false);
          setIsLoading(false);
        }
      );
    } catch (err) {
      // Fallback to non-streaming
      try {
        const result = await ragService.query({ query, includeContext: true, maxResults: 5 });
        setResponse(result);
        setStreamedAnswer(result.answer);
      } catch (fallbackErr) {
        setError('Failed to get an answer. Please try again.');
        console.error('RAG query error:', fallbackErr);
      } finally {
        setIsStreaming(false);
        setIsLoading(false);
      }
    }
  }, [query, isLoading]);

  // Resolve how stale each cited article is. Documents are skipped: they have
  // no review cycle, so an age on one would be the upload date wearing a
  // label that means something else.
  useEffect(() => {
    const articles = (response?.sources ?? []).filter((s) => s.type === 'article');
    if (articles.length === 0) return;

    let cancelled = false;
    void Promise.all(
      articles.map(async (source) => {
        try {
          const article = await knowledgeService.getArticle(source.id);
          return [source.id, article.updatedAt] as const;
        } catch {
          // Best-effort: an age we could not read is simply not shown.
          return null;
        }
      }),
    ).then((pairs) => {
      if (cancelled) return;
      const next: Record<string, string> = {};
      for (const pair of pairs) if (pair) next[pair[0]] = pair[1];
      setReviewedAt((prev) => ({ ...prev, ...next }));
    });

    return () => {
      cancelled = true;
    };
  }, [response]);

  // Record each completed search so it feeds the analytics dashboard.
  useEffect(() => {
    if (!response || !submittedQueryRef.current) return;
    knowledgeAnalyticsService
      .logSearch({
        query: submittedQueryRef.current,
        resultCount: response.sources?.length ?? 0,
        channel: 'agent',
        sessionId: sessionIdRef.current,
      })
      .catch(() => {
        /* analytics logging is best-effort */
      });
  }, [response]);

  const handleCancel = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }
    setIsStreaming(false);
    setIsLoading(false);
  }, []);

  const handleFeedback = useCallback(async (isHelpful: boolean) => {
    // Use conversationId from response, or fall back to our generated queryId
    const feedbackQueryId = response?.conversationId || queryIdRef.current;
    if (!feedbackQueryId || feedbackLoading) return;

    setFeedbackLoading(true);
    const rating = isHelpful ? 'helpful' : 'not_helpful';

    try {
      await ragService.submitFeedback({
        queryId: feedbackQueryId,
        helpful: isHelpful,
      });
      setFeedbackSent(rating);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      // Still show feedback as sent to user even if API fails (best-effort)
      setFeedbackSent(rating);
    } finally {
      setFeedbackLoading(false);
    }
  }, [response, feedbackLoading]);

  const handleSuggestionClick = useCallback((suggestion: string) => {
    setQuery(suggestion);
    setSuggestions([]);
    inputRef.current?.focus();
  }, []);

  const handleClear = useCallback(() => {
    setQuery('');
    setResponse(null);
    setStreamedAnswer('');
    setError(null);
    setSuggestions([]);
    setFeedbackSent(null);
    setFeedbackLoading(false);
    setCopied(false);
    inputRef.current?.focus();
  }, []);

  const handleCopyAnswer = useCallback(async () => {
    if (!streamedAnswer) return;
    try {
      await navigator.clipboard.writeText(streamedAnswer);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [streamedAnswer]);

  const handleRegenerate = useCallback(() => {
    if (!submittedQueryRef.current || isLoading) return;
    setQuery(submittedQueryRef.current);
    // Trigger a new search with the same query
    setTimeout(() => {
      handleSubmit();
    }, 0);
  }, [isLoading, handleSubmit]);

  const handleSourceClick = useCallback((source: RagSource, index = 0) => {
    if (source.type === 'article' && onArticleClick) {
      knowledgeAnalyticsService
        .logSearchClick({ sessionId: sessionIdRef.current, articleId: source.id, position: index })
        .catch(() => {
          /* analytics logging is best-effort */
        });
      onArticleClick(source.id);
    } else if (source.url) {
      window.open(source.url, '_blank');
    }
  }, [onArticleClick]);

  return (
    <div className="w-full">
      {/* Search Input */}
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center">
          <div className="absolute left-4 flex items-center pointer-events-none">
            <Sparkles className="h-5 w-5 text-primary-500" />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-12 pr-24 py-4 text-lg border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white text-gray-900 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500"
            disabled={isLoading}
          />
          <div className="absolute right-2 flex items-center gap-2">
            {query && !isLoading && (
              <button
                type="button"
                onClick={handleClear}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {isLoading ? (
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Cancel
              </button>
            ) : (
              <button
                type="submit"
                disabled={!query.trim()}
                className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Suggestions Dropdown */}
        {suggestions.length > 0 && !response && (
          <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 first:rounded-t-lg last:rounded-b-lg flex items-center gap-2"
              >
                <Search className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </form>

      {/* Response Area */}
      {(streamedAnswer || error) && (
        <div className="mt-6 space-y-4">
          {/* Error */}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
                  <button
                    onClick={handleRegenerate}
                    className="mt-2 text-sm text-red-600 dark:text-red-400 hover:underline flex items-center gap-1"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Try again
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Answer */}
          {streamedAnswer && (
            <div className="p-6 bg-gradient-to-br from-primary-50 to-white dark:from-gray-800 dark:to-gray-900 border border-primary-100 dark:border-gray-700 rounded-xl shadow-sm">
              <div className="flex items-center justify-between gap-2 mb-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-primary-100 dark:bg-primary-900/50 rounded-lg">
                    <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <span className="font-semibold text-gray-900 dark:text-white">AI Answer</span>
                  {isStreaming && <Loader2 className="h-4 w-4 animate-spin text-primary-500" />}
                </div>
                {response && (
                  <Badge variant={response.confidence > 0.7 ? 'success' : response.confidence > 0.4 ? 'warning' : 'default'}>
                    {Math.round(response.confidence * 100)}% confidence
                  </Badge>
                )}
              </div>

              {/* Low confidence warning */}
              {response && response.confidence < 0.4 && !isStreaming && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-700 dark:text-yellow-400">
                      This answer has low confidence. The information might not be accurate. Consider checking the sources below or submitting a support ticket for help.
                    </p>
                  </div>
                </div>
              )}

              <div className="prose prose-sm dark:prose-invert max-w-none">
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                  {streamedAnswer}
                </p>
              </div>

              {/* Follow-up Questions */}
              {response?.followUpQuestions && response.followUpQuestions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-primary-100 dark:border-gray-700">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Related questions:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {response.followUpQuestions.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setQuery(q);
                          handleSubmit();
                        }}
                        className="px-3 py-1 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-full hover:border-primary-500 hover:text-primary-600 transition-colors"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons & Feedback */}
              {!isStreaming && streamedAnswer && (
                <div className="mt-4 pt-4 border-t border-primary-100 dark:border-gray-700">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    {/* Left side - Action buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleCopyAnswer}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title="Copy answer"
                      >
                        {copied ? (
                          <>
                            <Check className="h-4 w-4 text-green-600" />
                            <span className="text-green-600">Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleRegenerate}
                        disabled={isLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                        title="Regenerate answer"
                      >
                        <RefreshCw className="h-4 w-4" />
                        <span>Regenerate</span>
                      </button>
                    </div>

                    {/* Right side - Feedback */}
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Was this helpful?
                      </span>
                      {feedbackSent ? (
                        <div className="flex items-center gap-2">
                          {feedbackSent === 'helpful' ? (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-sm">
                              <ThumbsUp className="h-4 w-4" />
                              <span>Thanks!</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm">
                              <ThumbsDown className="h-4 w-4" />
                              <span>Noted</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleFeedback(true)}
                            disabled={feedbackLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-gray-500 dark:text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                            title="Helpful"
                          >
                            {feedbackLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ThumbsUp className="h-4 w-4" />
                            )}
                            <span className="text-sm">Yes</span>
                          </button>
                          <button
                            onClick={() => handleFeedback(false)}
                            disabled={feedbackLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-gray-500 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                            title="Not helpful"
                          >
                            {feedbackLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <ThumbsDown className="h-4 w-4" />
                            )}
                            <span className="text-sm">No</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sources */}
          {response && !isStreaming && (
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowSources(!showSources)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                  <span className="font-medium text-gray-700 dark:text-gray-300">
                    Sources {response.sources?.length ? `(${response.sources.length})` : ''}
                  </span>
                </div>
                {showSources ? (
                  <ChevronUp className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                )}
              </button>

              {showSources && (
                <>
                  {response.sources && response.sources.length > 0 ? (
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                      {response.sources.map((source, index) => (
                        <button
                          key={index}
                          onClick={() => handleSourceClick(source, index)}
                          className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left group"
                        >
                          <div className="flex-shrink-0 p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg group-hover:bg-primary-200 dark:group-hover:bg-primary-900/50 transition-colors">
                            <FileText className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-medium text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                                {source.title}
                              </h4>
                              <Badge
                                variant={source.relevanceScore > 0.7 ? 'success' : source.relevanceScore > 0.4 ? 'warning' : 'default'}
                                className="text-xs"
                              >
                                {Math.round(source.relevanceScore * 100)}% match
                              </Badge>
                            </div>
                            {/* 18d. A retrieval score says how well this
                                source matched the question. It says nothing
                                about whether the source is still true, which
                                is the failure this line exists to catch. */}
                            {reviewedAt[source.id] && (
                              <div
                                className="font-mono mt-1 text-[10px] font-semibold uppercase tracking-[.08em]"
                                style={{
                                  color: isStale(reviewedAt[source.id])
                                    ? 'var(--co-terracotta)'
                                    : 'var(--co-ink-4)',
                                }}
                              >
                                {`Last reviewed ${daysSince(reviewedAt[source.id])} days ago`}
                              </div>
                            )}
                            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">
                              {source.excerpt}
                            </p>
                          </div>
                          <ExternalLink className="flex-shrink-0 h-4 w-4 text-gray-400 dark:text-gray-500 group-hover:text-primary-500 transition-colors" />
                        </button>
                      ))}
                      {(() => {
                        const stale = (response.sources ?? []).filter(
                          (s) => reviewedAt[s.id] && isStale(reviewedAt[s.id]),
                        );
                        if (stale.length === 0) return null;
                        return (
                          <div className="px-4 py-3 text-[12px] leading-[1.5] text-[var(--co-terracotta)]">
                            {stale.length === 1
                              ? `The assistant is citing an article nobody has checked in ${daysSince(reviewedAt[stale[0].id])} days.`
                              : `${stale.length} of these sources have gone unreviewed for over 60 days.`}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="px-4 py-6 text-center">
                      <FileText className="h-8 w-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        No matching sources found in the knowledge base.
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        The AI generated this response based on general knowledge.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Starter suggestions when no search performed */}
      {!streamedAnswer && !error && !isLoading && showSuggestions && (
        <div className="mt-6">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Try asking questions like:
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              'How do I get started?',
              'What features are available?',
              'How to reset my password?',
              'Troubleshooting common issues',
            ].map((suggestion, index) => (
              <button
                key={index}
                onClick={() => {
                  setQuery(suggestion);
                  inputRef.current?.focus();
                }}
                className="px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-500 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-colors flex items-center gap-2"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default RagSearch;
